<template>
	<div class="stock-display-rule">
		<div class="page-tips">
			<p class="tips-title"><span class="tip-label">说明</span>库存推送数量按以下规则计算</p>
			<ul class="tips-list">
				<li>单渠道最小数量：渠道库存低于该值时，按 0 件计算</li>
				<li>渠道最大数量：0 表示不限制上限</li>
				<li>推送比率：填写百分比，100 表示不限制，0 表示推送 0 库存</li>
				<li>小数处理方式：用于推送库存计算结果含小数时的取整规则</li>
			</ul>
			<p class="tips-example">举例：渠道库存 8，最小 1、最大 10、比率 100%、四舍五入 → 推送 8</p>
		</div>

		<div class="form-section">
			<el-form ref="form" :model="form" :rules="rules" label-width="150px" class="config-form">
				<el-form-item :label="platformShopNameLabel" prop="shopName">
					<div class="field-wrap">
						<el-input v-model="form.shopName" placeholder="请输入店铺名称" disabled />
					</div>
				</el-form-item>
				<el-form-item label="渠道" prop="channelId">
					<div class="field-wrap">
						<el-select v-model="form.channelId" placeholder="请选择" filterable :loading="channelLoading">
							<el-option
								v-for="item in channelOptions"
								:key="item.id"
								:label="formatChannelOptionLabel(item)"
								:value="item.id"
							/>
						</el-select>
					</div>
				</el-form-item>
				<el-form-item label="单渠道最小数量" prop="minQty">
					<div class="field-wrap">
						<el-input v-model="form.minQty" placeholder="请填写最小数量" />
						<p class="field-tip">渠道库存小于该值时，按 0 件计算</p>
					</div>
				</el-form-item>
				<el-form-item label="渠道最大数量" prop="maxQty">
					<div class="field-wrap">
						<el-input v-model="form.maxQty" placeholder="请填写最大数量" />
						<p class="field-tip">0 表示不控制最大数量</p>
					</div>
				</el-form-item>
				<el-form-item label="推送比率 (%)" prop="pushRatio">
					<div class="field-wrap">
						<el-input v-model="form.pushRatio" placeholder="请填写推送比率" />
						<p class="field-tip">100 表示不限制，0 表示推送 0 库存</p>
					</div>
				</el-form-item>
				<el-form-item label="库存-小数处理方式" prop="decimalType">
					<div class="field-wrap">
						<el-select v-model="form.decimalType" placeholder="请选择">
							<el-option
								v-for="item in decimalOptions"
								:key="item.value"
								:label="item.label"
								:value="item.value"
							/>
						</el-select>
						<p class="field-tip">推送库存 = 渠道库存 × 推送比率，结果含小数时按此规则处理</p>
					</div>
				</el-form-item>
				<div class="form-actions">
					<el-button type="primary" :loading="saving" @click="onSave">保存</el-button>
				</div>
			</el-form>
		</div>

		<div class="table-section">
			<el-table
				ref="table"
				v-loading="listLoading"
				:data="pageList"
				style="width: 100%"
				@selection-change="onSelectionChange"
			>
				<el-table-column type="selection" width="48" align="center" />
				<el-table-column type="index" label="序号" width="64" align="center" :index="indexMethod" />
				<el-table-column prop="shop_name" :label="platformShopColumnLabel" min-width="150" show-overflow-tooltip />
				<el-table-column prop="channel_code" label="渠道编码" min-width="90" />
				<el-table-column prop="channel_name" label="渠道名称" min-width="110" show-overflow-tooltip />
				<el-table-column label="单渠道最小数量" min-width="220">
					<template slot-scope="scope">
						<div class="min-qty-cell">
							<span class="min-qty-value">{{ scope.row.min_qty }}</span>
							<span class="min-qty-tip">（库存 &lt; {{ scope.row.min_qty }} 时按 0 件计算）</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="渠道最大数量" min-width="180">
					<template slot-scope="scope">
						<div class="max-qty-cell">
							<template v-if="isMaxQtyUnlimited(scope.row.max_qty)">
								<span class="max-qty-unlimited">不限制</span>
								<span class="max-qty-tip">（0 表示不控制上限）</span>
							</template>
							<template v-else>
								<span class="max-qty-value">{{ scope.row.max_qty }}</span>
								<span class="max-qty-tip">（推送上限 {{ scope.row.max_qty }} 件）</span>
							</template>
						</div>
					</template>
				</el-table-column>
				<el-table-column prop="push_ratio" label="推送比率" width="90" align="center" />
				<el-table-column label="状态" width="128" align="center">
					<template slot-scope="scope">
						<div class="status-cell" :class="scope.row.state === 1 ? 'is-on' : 'is-off'">
							<el-switch
								v-model="scope.row.state"
								:active-value="1"
								:inactive-value="2"
								active-color="#ff4714"
								inactive-color="#dcdfe6"
								@change="onStateChange(scope.row)"
							/>
							<span class="status-label">{{ scope.row.state === 1 ? '启用' : '禁用' }}</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column prop="decimal_label" label="小数处理方式" width="110" align="center" />
				<el-table-column label="操作" width="72" align="center" fixed="right">
					<template slot-scope="scope">
						<span class="action-link" @click="onDelete(scope.row)">删除</span>
					</template>
				</el-table-column>
			</el-table>

			<div class="page-bar">
				<el-pagination
					v-if="total > 0"
					background
					layout="total, sizes, prev, pager, next, jumper"
					:current-page.sync="page"
					:page-size="pageSize"
					:page-sizes="pageSizes"
					:total="total"
					@current-change="onPageChange"
					@size-change="onPageSizeChange"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import {
	ALL_CHANNEL_OPTION,
	buildAllocationSavePayload,
	deleteShopAllocation,
	fetchShopAllocationList,
	getRetailShopId,
	saveShopAllocation,
	saveShopAllocationState,
} from './retailShopAllocation'
import {
	fetchAllChannels,
	formatChannelLabel,
} from './retailShopChannel'
import taobaoPlatformMixin from './taobaoPlatformMixin'

export default {
	name: 'StockDisplayRule',
	mixins: [taobaoPlatformMixin],
	inject: {
		retailShop: { default: null },
	},
	data() {
		return {
			form: {
				shopName: '',
				channelId: 0,
				minQty: '',
				maxQty: '',
				pushRatio: '',
				decimalType: 'round',
			},
			rules: {
				shopName: [{ required: true, message: '请输入店铺名称', trigger: 'blur' }],
				channelId: [{ required: true, message: '请选择渠道', trigger: 'change' }],
				minQty: [{ required: true, message: '请填写单渠道最小数量', trigger: 'blur' }],
				maxQty: [{ required: true, message: '请填写渠道最大数量', trigger: 'blur' }],
				pushRatio: [{ required: true, message: '请填写推送比率', trigger: 'blur' }],
				decimalType: [{ required: true, message: '请选择小数处理方式', trigger: 'change' }],
			},
			channelOptions: [ALL_CHANNEL_OPTION],
			channelLoading: false,
			saving: false,
			listLoading: false,
			decimalOptions: [
				{ value: 'round', label: '四舍五入' },
				{ value: 'floor', label: '向下取整' },
				{ value: 'ceil', label: '向上取整' },
			],
			list: [],
			displayList: [],
			selectedRows: [],
			page: 1,
			pageSize: 10,
			pageSizes: [10, 20, 50],
			total: 0,
		}
	},
	computed: {
		pageList() {
			const start = (this.page - 1) * this.pageSize
			return this.displayList.slice(start, start + this.pageSize)
		},
	},
	mounted() {
		this.syncShopName()
		this.fetchChannelOptions()
	},
	watch: {
		'retailShop.shopDetail': {
			immediate: true,
			handler() {
				this.syncShopName()
			},
		},
		'retailShop.currentShopId': {
			immediate: true,
			handler(id) {
				if (id) {
					this.fetchList()
				} else {
					this.list = []
					this.applyList()
				}
			},
		},
	},
	methods: {
		syncShopName() {
			if (this.retailShop && this.retailShop.shopName) {
				this.form.shopName = this.retailShop.shopName
			}
		},
		formatChannelOptionLabel(item) {
			if (item.id === 0) {
				return item.name
			}
			return formatChannelLabel(item)
		},
		fetchChannelOptions() {
			this.channelLoading = true
			fetchAllChannels(this.$axios).then(list => {
				this.channelOptions = [ALL_CHANNEL_OPTION].concat(list)
			}).catch(err => {
				console.log(err)
				this.$message.error(err.message || '获取渠道列表失败')
			}).finally(() => {
				this.channelLoading = false
			})
		},
		fetchList() {
			const shopId = getRetailShopId(this.retailShop)
			if (!shopId) {
				return
			}
			this.listLoading = true
			fetchShopAllocationList(this.$axios, shopId, {
				page: 1,
				limit: 0,
			}).then(list => {
				this.list = list
				this.applyList()
			}).catch(err => {
				console.log(err)
				this.$message.error(err.message || '获取库存显示规则列表失败')
			}).finally(() => {
				this.listLoading = false
			})
		},
		applyList() {
			this.displayList = [...this.list]
			this.total = this.list.length
			const totalPages = Math.max(1, Math.ceil(this.total / this.pageSize))
			if (this.page > totalPages) {
				this.page = totalPages
			}
		},
		indexMethod(index) {
			return (this.page - 1) * this.pageSize + index + 1
		},
		isMaxQtyUnlimited(value) {
			return value === 0 || value === '0'
		},
		getDecimalLabel(value) {
			const item = this.decimalOptions.find(opt => opt.value === value)
			return item ? item.label : ''
		},
		onSelectionChange(rows) {
			this.selectedRows = rows
		},
		onSave() {
			this.$refs.form.validate((valid) => {
				if (!valid) return
				const shopId = getRetailShopId(this.retailShop)
				if (!shopId) {
					this.$message.warning('店铺信息不完整')
					return
				}
				this.saving = true
				const payload = buildAllocationSavePayload(shopId, this.form)
				saveShopAllocation(this.$axios, payload).then(() => {
					this.form.minQty = ''
					this.form.maxQty = ''
					this.form.pushRatio = ''
					this.$nextTick(() => {
						if (this.$refs.form) {
							this.$refs.form.clearValidate(['minQty', 'maxQty', 'pushRatio'])
						}
					})
					this.fetchList()
					this.$message.success('保存成功')
				}).catch(err => {
					console.log(err)
					this.$message.error(err.message || '保存失败')
				}).finally(() => {
					this.saving = false
				})
			})
		},
		onStateChange(row) {
			if (!row.id) {
				this.$message.warning('配置信息不完整')
				return
			}
			const newState = row.state
			const oldState = newState === 1 ? 2 : 1
			saveShopAllocationState(this.$axios, row, newState).then(() => {
				const item = this.list.find(entry => entry.id === row.id)
				if (item) {
					item.state = newState
				}
				this.$message.success('状态已更新')
			}).catch(err => {
				row.state = oldState
				console.log(err)
				this.$message.error(err.message || '状态更新失败')
			})
		},
		onDelete(row) {
			if (!row.id) {
				this.$message.warning('配置信息不完整')
				return
			}
			this.$confirm('确定删除该规则吗？', '提示', {
				type: 'warning',
			}).then(() => {
				deleteShopAllocation(this.$axios, row.id).then(() => {
					this.fetchList()
					this.$message.success('删除成功')
				}).catch(err => {
					console.log(err)
					this.$message.error(err.message || '删除失败')
				})
			}).catch(() => {})
		},
		onPageChange() {
			this.applyList()
		},
		onPageSizeChange() {
			this.page = 1
			this.applyList()
		},
	},
}
</script>

<style scoped lang="less">
@import '../storeTable.less';
.stock-display-rule {
	padding: 24px 28px 36px;
	background: #fff;
	min-height: 100%;
	box-sizing: border-box;
}

.page-tips {
	margin-bottom: 28px;
	padding: 16px 20px;
	background: #fafbfc;
	border-radius: 6px;
	border: 1px solid #f0f2f5;

	.tips-title {
		margin: 0 0 10px;
		font-size: 13px;
		line-height: 1.6;
		color: #606266;
	}

	.tip-label {
		display: inline-block;
		margin-right: 6px;
		padding: 1px 8px;
		font-size: 12px;
		color: #e6a23c;
		background: rgba(230, 162, 60, 0.1);
		border-radius: 3px;
	}

	.tips-list {
		margin: 0;
		padding-left: 18px;
		font-size: 13px;
		line-height: 1.9;
		color: #909399;

		li {
			margin: 0;
		}
	}

	.tips-example {
		margin: 10px 0 0;
		padding-top: 10px;
		border-top: 1px dashed #ebeef5;
		font-size: 12px;
		color: #b0b3b8;
		line-height: 1.6;
	}
}

.form-section {
	margin-bottom: 32px;
	padding-bottom: 28px;
	border-bottom: 1px solid #f2f3f5;
}

.config-form {
	max-width: 640px;

	/deep/ .el-form-item {
		margin-bottom: 18px;
	}

	/deep/ .el-form-item__label {
		font-size: 12px;
		color: #606266;
		line-height: 36px;
		padding-right: 16px;
	}

	/deep/ .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label::before {
		color: #f56c6c;
	}
}

.field-wrap {
	max-width: 360px;

	.el-input,
	.el-select {
		width: 100%;
	}

	/deep/ .el-input__inner {
		height: 36px;
		line-height: 36px;
		border-color: #e8eaed;
		border-radius: 4px;
		background: #fafbfc;
		transition: all 0.2s;

		&:hover {
			border-color: #dcdfe6;
			background: #fff;
		}

		&:focus {
			border-color: #ff4714;
			background: #fff;
		}
	}
}

.field-tip {
	margin: 6px 0 0;
	font-size: 11px;
	color: #b0b3b8;
	line-height: 1.5;
}

.form-actions {
	display: flex;
	justify-content: center;
	max-width: 640px;
	margin-top: 8px;

	.el-button {
		min-width: 96px;
		height: 36px;
		padding: 0 24px;
		font-size: 12px;
		border-radius: 4px;
		letter-spacing: 1px;
	}
}

.table-section {
	.table-section-border();

	/deep/ .el-table {
		font-size: 13px;
		color: #303133;

		&::before {
			height: 0;
		}

		thead {
			font-weight: normal;
		}

		th.el-table__cell {
			background: #fafbfc;
			color: #606266;
			font-weight: normal;
			font-size: 13px;
			padding: 10px 0;
		}

		td.el-table__cell {
			font-size: 13px;
			padding: 10px 0;
		}

		.cell {
			line-height: 1.5;
		}

		tr:hover > td.el-table__cell {
			background: #fafbfc;
		}

		.el-table__empty-text {
			font-size: 13px;
			color: #909399;
		}
	}
}

.min-qty-cell {
	display: inline-flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 4px;
	font-size: 13px;
	line-height: 1.5;
}

.min-qty-value {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	padding: 0 8px;
	height: 22px;
	font-size: 13px;
	font-weight: 600;
	color: #ff4714;
	background: rgba(255, 71, 20, 0.08);
	border-radius: 4px;
	line-height: 1;
}

.min-qty-tip {
	font-size: 12px;
	color: #909399;
}

.max-qty-cell {
	display: inline-flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 4px;
	font-size: 13px;
	line-height: 1.5;
}

.max-qty-value {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	padding: 0 8px;
	height: 22px;
	font-size: 13px;
	font-weight: 600;
	color: #409eff;
	background: rgba(64, 158, 255, 0.1);
	border-radius: 4px;
	line-height: 1;
}

.max-qty-unlimited {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0 10px;
	height: 22px;
	font-size: 12px;
	font-weight: 500;
	color: #909399;
	background: #f5f6f8;
	border-radius: 4px;
	line-height: 1;
}

.max-qty-tip {
	font-size: 12px;
	color: #909399;
}

.status-cell {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 4px 12px 4px 8px;
	border-radius: 20px;
	transition: background 0.2s, box-shadow 0.2s;

	/deep/ .el-switch {
		height: 20px;
		line-height: 20px;

		.el-switch__core {
			width: 36px !important;
			height: 20px;
			border-radius: 10px;

			&::after {
				width: 16px;
				height: 16px;
			}
		}

		&.is-checked .el-switch__core::after {
			margin-left: -17px;
		}
	}

	&.is-on {
		background: rgba(255, 71, 20, 0.1);

		.status-label {
			color: #ff4714;
		}
	}

	&.is-off {
		background: #f5f6f8;

		.status-label {
			color: #909399;
		}
	}
}

.status-label {
	font-size: 13px;
	font-weight: 500;
	line-height: 1;
	letter-spacing: 0.5px;
	user-select: none;
}

.action-link {
	font-size: 13px;
	color: #ff4714;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.7;
	}
}

.page-bar {
	display: flex;
	justify-content: flex-end;
	margin-top: 24px;
	min-height: 32px;
}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/detailed/stockDisplayRule.vue